import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Button,
  MobileStepper,
} from '@mui/material';
import {
  KeyboardArrowLeft,
  KeyboardArrowRight,
  CheckCircle as CheckIcon,
} from '@mui/icons-material';
import { markEducationModalSeen, type EducationModalId } from '../../lib/educationModals';

export interface EducationStep {
  title: string;
  description: string;
  icon: React.ReactNode;
  visual: React.ReactNode;
}

interface EducationModalProps {
  open: boolean;
  onClose: () => void;
  modalId: EducationModalId;
  steps: EducationStep[];
  /** Header background color - defaults to primary.main */
  headerColor?: string;
  /** Final button text - defaults to "Get Started" */
  finishButtonText?: string;
  /** Button color variant - defaults to "primary" */
  buttonColor?: 'primary' | 'secondary' | 'success' | 'error' | 'info' | 'warning';
}

export const EducationModal: React.FC<EducationModalProps> = ({
  open,
  onClose,
  modalId,
  steps,
  headerColor = 'primary.main',
  finishButtonText = 'Get Started',
  buttonColor = 'primary',
}) => {
  const [activeStep, setActiveStep] = useState(0);

  const handleNext = () => {
    if (activeStep === steps.length - 1) {
      handleComplete();
    } else {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleComplete = () => {
    markEducationModalSeen(modalId);
    setActiveStep(0);
    onClose();
  };

  const handleSkip = () => {
    markEducationModalSeen(modalId);
    setActiveStep(0);
    onClose();
  };

  if (!open) return null;

  const currentStep = steps[activeStep];

  return (
    <Dialog
      open={open}
      onClose={handleSkip}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2, overflow: 'hidden' },
      }}
    >
      <Box
        sx={{
          bgcolor: headerColor,
          color: 'white',
          py: 3,
          px: 3,
          textAlign: 'center',
        }}
      >
        <Box sx={{ mb: 1 }}>{currentStep.icon}</Box>
        <Typography variant="h5" fontWeight="bold">
          {currentStep.title}
        </Typography>
      </Box>

      <DialogContent sx={{ py: 4, px: 3 }}>
        <Box sx={{ mb: 3, minHeight: 100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {currentStep.visual}
        </Box>
        <Typography variant="body1" textAlign="center" color="text.secondary">
          {currentStep.description}
        </Typography>
      </DialogContent>

      <Box sx={{ px: 3, pb: 2 }}>
        <MobileStepper
          variant="dots"
          steps={steps.length}
          position="static"
          activeStep={activeStep}
          sx={{ bgcolor: 'transparent', justifyContent: 'center', mb: 2 }}
          backButton={<Box />}
          nextButton={<Box />}
        />
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            size="small"
            onClick={handleSkip}
            sx={{ color: 'text.secondary' }}
            data-testid="education-modal-skip"
          >
            Skip
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button
            size="small"
            onClick={handleBack}
            disabled={activeStep === 0}
            startIcon={<KeyboardArrowLeft />}
          >
            Back
          </Button>
          <Button
            variant="contained"
            color={buttonColor}
            onClick={handleNext}
            endIcon={activeStep === steps.length - 1 ? <CheckIcon /> : <KeyboardArrowRight />}
          >
            {activeStep === steps.length - 1 ? finishButtonText : 'Next'}
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
};

export default EducationModal;
